import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Send, Users, CheckCircle2, AlertCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { useWelcomeEmailTemplates } from "@/hooks/useWelcomeEmailTemplates";

interface SelectedUser {
  id: string;
  full_name: string;
  email: string | null;
}

interface SendImportedWelcomeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUsers: SelectedUser[];
  onComplete: () => void;
}

interface JobStatus {
  id: string;
  status: string;
  total_count: number;
  processed_count: number;
  successful_count: number;
  failed_count: number;
  completed_at: string | null;
}

export function SendImportedWelcomeDialog({
  open,
  onOpenChange,
  selectedUsers,
  onComplete,
}: SendImportedWelcomeDialogProps) {
  const { toast } = useToast();
  const { data: templates, isLoading: templatesLoading } = useWelcomeEmailTemplates();
  const [isSending, setIsSending] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>("custom");
  const [customSubject, setCustomSubject] = useState("Welcome to EAB Connect!");
  const [customBody, setCustomBody] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const usersWithEmail = selectedUsers.filter(u => u.email);
  const usersWithoutEmail = selectedUsers.filter(u => !u.email);

  // Set default template when templates load
  useEffect(() => {
    if (templates && templates.length > 0 && selectedTemplateKey === "custom") {
      setSelectedTemplateKey(templates[0].key);
    }
  }, [templates]);

  // Cleanup polling on unmount or dialog close
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  // Poll for job status using direct database query (avoids edge function load/CORS issues)
  useEffect(() => {
    if (!jobId || !open) return;

    const pollStatus = async () => {
      try {
        const { data: job, error } = await supabase
          .from('welcome_email_jobs')
          .select('*')
          .eq('id', jobId)
          .single();
        
        if (error) {
          console.error('Error polling job status:', error);
          return;
        }
        
        if (job) {
          setJobStatus({
            id: job.id,
            status: job.status,
            total_count: job.total_count,
            processed_count: job.processed_count,
            successful_count: job.successful_count,
            failed_count: job.failed_count,
            completed_at: job.completed_at
          });
          
          // Stop polling if complete, failed, or aborted
          if (job.status === 'completed' || job.status === 'failed' || job.status === 'aborted') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            
            if (job.status === 'completed') {
              toast({
                title: "Welcome emails sent",
                description: `Successfully sent ${job.successful_count} of ${job.total_count} emails.${job.failed_count > 0 ? ` ${job.failed_count} failed.` : ''}`,
              });
              onComplete();
            }
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    };

    // Start polling
    pollStatus();
    pollingRef.current = setInterval(pollStatus, 2000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobId, open, toast, onComplete]);

  const selectedTemplate = templates?.find(t => t.key === selectedTemplateKey);

  const getSubject = () => {
    if (selectedTemplateKey === "custom") {
      return customSubject || "Welcome to EAB Connect!";
    }
    return selectedTemplate?.subject || "Welcome to EAB Connect!";
  };

  const getBody = () => {
    if (selectedTemplateKey === "custom") {
      return customBody;
    }
    return selectedTemplate?.body || "";
  };

  const handleSend = async () => {
    if (usersWithEmail.length === 0) {
      toast({
        title: "No valid users",
        description: "None of the selected users have email addresses.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    setJobStatus(null);
    setJobId(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-imported-welcome`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionData.session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'start',
            userIds: usersWithEmail.map(u => u.id),
            context: getBody(),
            customSubject: getSubject(),
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      
      if (result.jobId) {
        setJobId(result.jobId);
        setJobStatus({
          id: result.jobId,
          status: result.isComplete ? 'completed' : 'processing',
          total_count: result.total,
          processed_count: result.processed,
          successful_count: result.successful,
          failed_count: result.failed,
          completed_at: null
        });
        
        if (result.isComplete) {
          toast({
            title: "Welcome emails sent",
            description: `Successfully sent ${result.successful} of ${result.total} emails.${result.failed > 0 ? ` ${result.failed} failed.` : ''}`,
          });
          onComplete();
          setIsSending(false);
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error sending welcome emails:', error);
      toast({
        title: "Failed to start email job",
        description: errorMessage,
        variant: "destructive",
      });
      setIsSending(false);
    }
  };

  const handleTemplateChange = (value: string) => {
    setSelectedTemplateKey(value);
    if (value === "custom") {
      setCustomSubject("Welcome to EAB Connect!");
      setCustomBody("");
    }
  };

  const handleClose = () => {
    if (jobStatus?.status === 'processing') {
      // Allow closing but job continues in background
      toast({
        title: "Sending continues in background",
        description: "Emails will continue to be sent. You can check progress in the admin logs.",
      });
    }
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setJobId(null);
    setJobStatus(null);
    setIsSending(false);
    onOpenChange(false);
  };

  const progressPercent = jobStatus 
    ? Math.round((jobStatus.processed_count / jobStatus.total_count) * 100)
    : 0;

  const isJobInProgress = jobStatus?.status === 'processing' || jobStatus?.status === 'pending';
  const isJobComplete = jobStatus?.status === 'completed';
  const isJobFailed = jobStatus?.status === 'failed';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Welcome Emails
          </DialogTitle>
          <DialogDescription>
            Send customizable welcome emails with login links to imported users.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden space-y-4">
          {/* Job Progress */}
          {jobStatus && (
            <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isJobInProgress && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                  {isJobComplete && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                  {isJobFailed && <AlertCircle className="h-4 w-4 text-destructive" />}
                  <span className="font-medium">
                    {isJobInProgress && "Sending emails..."}
                    {isJobComplete && "Complete!"}
                    {isJobFailed && "Failed"}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {jobStatus.processed_count} of {jobStatus.total_count}
                </span>
              </div>
              <Progress value={progressPercent} className="h-2" />
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span className="text-emerald-600 dark:text-emerald-400">✓ {jobStatus.successful_count} sent</span>
                {jobStatus.failed_count > 0 && (
                  <span className="text-destructive">✗ {jobStatus.failed_count} failed</span>
                )}
              </div>
            </div>
          )}

          {/* Selected Users */}
          {!jobStatus && (
            <>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Recipients ({usersWithEmail.length} users)
                </Label>
                <ScrollArea className="h-24 border rounded-md p-2">
                  <div className="flex flex-wrap gap-1.5">
                    {usersWithEmail.map((user) => (
                      <Badge key={user.id} variant="secondary" className="text-xs">
                        {user.full_name || user.email}
                      </Badge>
                    ))}
                  </div>
                </ScrollArea>
                {usersWithoutEmail.length > 0 && (
                  <p className="text-xs text-destructive">
                    {usersWithoutEmail.length} user(s) will be skipped (no email address)
                  </p>
                )}
              </div>

              {/* Template Selection */}
              <div className="space-y-2">
                <Label htmlFor="template">Email Template</Label>
                <Select value={selectedTemplateKey} onValueChange={handleTemplateChange} disabled={templatesLoading || isSending}>
                  <SelectTrigger id="template">
                    {templatesLoading ? (
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading templates...
                      </span>
                    ) : (
                      <SelectValue placeholder="Select a template" />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {templates?.map((template) => (
                      <SelectItem key={template.key} value={template.key}>
                        {template.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom">Custom Message</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Subject Line */}
              <div className="space-y-2">
                <Label htmlFor="subject">Subject Line</Label>
                <Input
                  id="subject"
                  value={getSubject()}
                  onChange={(e) => {
                    if (selectedTemplateKey === "custom") {
                      setCustomSubject(e.target.value);
                    }
                  }}
                  disabled={selectedTemplateKey !== "custom" || isSending}
                  className={selectedTemplateKey !== "custom" ? "bg-muted" : ""}
                />
              </div>

              {/* Email Body / Context */}
              <div className="space-y-2">
                <Label htmlFor="body">Message Context</Label>
                <Textarea
                  id="body"
                  value={getBody()}
                  onChange={(e) => {
                    if (selectedTemplateKey === "custom") {
                      setCustomBody(e.target.value);
                    }
                  }}
                  disabled={selectedTemplateKey !== "custom" || isSending}
                  className={`min-h-[100px] ${selectedTemplateKey !== "custom" ? "bg-muted" : ""}`}
                  placeholder="Enter your custom message..."
                />
                <p className="text-xs text-muted-foreground">
                  This message will appear in the email. Each user will receive a unique login link.
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} disabled={false}>
            {isJobInProgress ? "Close (continues in background)" : "Close"}
          </Button>
          {!jobStatus && (
            <Button onClick={handleSend} disabled={isSending || usersWithEmail.length === 0}>
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {usersWithEmail.length} User{usersWithEmail.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
